import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Editor from 'react-simple-code-editor';
import Prism from 'prismjs';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-jsx';
import 'prismjs/themes/prism.css';
import { formatCode } from '../../utils/formatCode';

/**
 * Code input textarea component
 * Optimized for code editing with syntax highlighting support
 */
const CodeInput = ({ 
  value,
  onChange,
  placeholder = 'Enter code...',
  language = 'html',
  className = '',
  rows = 8,
  disabled = false,
  ...props 
}) => {
  const baseClasses = "w-full border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-purple-500 focus:border-transparent font-mono text-sm transition-colors bg-white";
  const disabledClasses = disabled ? "opacity-60 cursor-not-allowed" : "";
  const [isFormatting, setIsFormatting] = useState(false);

  const prismLang = useMemo(() => {
    const lang = (language || 'html').toLowerCase();
    if (lang === 'css') return Prism.languages.css;
    if (lang === 'javascript' || lang === 'js') return Prism.languages.javascript;
    if (lang === 'jsx') return Prism.languages.jsx || Prism.languages.javascript;
    return Prism.languages.markup;
  }, [language]);

  const handleFormat = useCallback(async () => {
    if (disabled) return;
    try {
      setIsFormatting(true);
      const formatted = await formatCode(language, value || '');
      onChange(formatted);
    } finally {
      setIsFormatting(false);
    }
  }, [disabled, language, onChange, value]);

  return (
    <div className={`${baseClasses} ${disabledClasses} ${className}`} {...props}>
      <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 rounded-t-lg">
        <span className="text-xs uppercase tracking-wide text-gray-500">{language}</span>
        <button
          type="button"
          onClick={handleFormat}
          disabled={disabled || isFormatting}
          className="px-2 py-1 text-xs rounded-md bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60"
        >
          {isFormatting ? 'Formatting…' : 'Format'}
        </button>
      </div>
      <Editor
        value={value}
        onValueChange={onChange}
        highlight={(code) => Prism.highlight(code, prismLang, (language || 'markup').toLowerCase())}
        padding={12}
        textareaId="code-input"
        // react-simple-code-editor doesn't natively support placeholder
        readOnly={disabled}
        textareaClassName="outline-none"
        preClassName={`language-${(language || 'markup').toLowerCase()}`} 
        style={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
          fontSize: 13,
          minHeight: `${Math.max(rows, 4) * 18}px`,
          background: 'white',
          borderBottomLeftRadius: '0.5rem',
          borderBottomRightRadius: '0.5rem'
        }}
      />
    </div>
  );
};

CodeInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  language: PropTypes.oneOf(['html', 'css', 'javascript', 'jsx']),
  className: PropTypes.string,
  rows: PropTypes.number,
  disabled: PropTypes.bool
};

export default CodeInput;
